import { universalFields, mediaRadiusField } from '../_shared/universal.js';
/**
 * product-gallery — the large product-image gallery for the product template. Renders the route product's
 * images (or the built-in demo in the builder preview) as a main image + thumbnail strip, with a
 * configurable layout, aspect ratio, optional pure-CSS hover-zoom, and overlaid "badge" blocks.
 *
 * Grouping (deep-config infra): Layout / Style / the shared "Section" universal group. Repeatable
 * `badge` blocks overlay a coloured pill on the main image (e.g. "New", "-30%").
 */
export const productGallerySchema = {
    type: 'product-gallery',
    name: 'Product gallery',
    version: 1,
    templates: ['product'],
    fields: [
        // ── Layout ──────────────────────────────────────────────────────────────
        {
            id: 'galleryLayout',
            type: 'select',
            label: 'Desktop layout',
            group: 'Layout',
            info: 'Shrine media-gallery layouts.',
            default: 'thumbnail',
            options: [
                { value: 'thumbnail', label: 'Thumbnails + main image' },
                { value: 'thumbnail-slider', label: 'Thumbnail carousel' },
                { value: 'stacked', label: 'Stacked (all images)' },
                { value: 'columns', label: '2 columns (all images)' },
            ],
        },
        {
            id: 'mediaSize',
            type: 'select',
            label: 'Gallery size',
            group: 'Layout',
            default: 'large',
            options: [
                { value: 'small', label: 'Small' },
                { value: 'medium', label: 'Medium' },
                { value: 'large', label: 'Large (full width)' },
            ],
        },
        {
            id: 'mediaFit',
            type: 'select',
            label: 'Image fit',
            group: 'Layout',
            info: 'Cover crops to fill the frame; contain shows the whole image (letterboxed).',
            default: 'cover',
            options: [
                { value: 'cover', label: 'Cover (crop to fill)' },
                { value: 'contain', label: 'Contain (fit whole image)' },
            ],
        },
        {
            id: 'thumbnailPosition',
            type: 'select',
            label: 'Thumbnail position',
            group: 'Layout',
            default: 'bottom',
            options: [
                { value: 'left', label: 'Left of image' },
                { value: 'bottom', label: 'Below image' },
            ],
            visibleWhen: { field: 'galleryLayout', equals: ['thumbnail', 'thumbnail-slider'] },
        },
        {
            id: 'aspectRatio',
            type: 'select',
            label: 'Image ratio',
            group: 'Layout',
            default: 'portrait',
            options: [
                { value: 'square', label: 'Square (1:1)' },
                { value: 'portrait', label: 'Portrait (4:5)' },
                { value: 'landscape', label: 'Landscape (3:2)' },
            ],
        },
        {
            id: 'showThumbnails',
            type: 'checkbox',
            label: 'Show thumbnails',
            group: 'Layout',
            default: true,
            visibleWhen: { field: 'galleryLayout', equals: ['thumbnail', 'thumbnail-slider'] },
        },
        {
            id: 'zoom',
            type: 'checkbox',
            label: 'Hover zoom',
            group: 'Layout',
            info: 'Pure-CSS zoom on the image when the cursor hovers it.',
            default: true,
        },
        // ── Style ───────────────────────────────────────────────────────────────
        {
            id: 'borderRadius',
            type: 'select',
            label: 'Corner radius',
            group: 'Style',
            default: 'theme',
            options: [
                { value: 'theme', label: 'Theme default' },
                { value: 'none', label: 'None' },
                { value: 'sm', label: 'Small' },
                { value: 'md', label: 'Medium' },
                { value: 'lg', label: 'Large' },
            ],
        },
        // ── Section (universal) ──────────────────────────────────────────────────
        mediaRadiusField,
        ...universalFields,
    ],
    blocks: [
        {
            type: 'badge',
            name: 'Badge',
            max: 3,
            fields: [
                { id: 'text', type: 'text', label: 'Label', default: 'Nouveau', maxLength: 24 },
                {
                    id: 'color',
                    type: 'color',
                    label: 'Color',
                    default: { ref: 'custom', value: '#0a7c5a' },
                },
                {
                    id: 'position',
                    type: 'select',
                    label: 'Position',
                    default: 'top-left',
                    options: [
                        { value: 'top-left', label: 'Top left' },
                        { value: 'top-right', label: 'Top right' },
                        { value: 'bottom-left', label: 'Bottom left' },
                        { value: 'bottom-right', label: 'Bottom right' },
                    ],
                },
            ],
        },
    ],
    maxBlocks: 3,
};
export const productGalleryPresets = [
    {
        name: 'Product gallery',
        // A centred medium gallery reads as a premium product shot (not an overwhelming full-bleed image);
        // thumbnails below, portrait crop, hover zoom.
        settings: {
            galleryLayout: 'thumbnail',
            mediaSize: 'medium',
            mediaFit: 'cover',
            thumbnailPosition: 'bottom',
            aspectRatio: 'portrait',
            zoom: true,
        },
        blocks: [{ type: 'badge', settings: { text: '@t:section.new_badge', position: 'top-left' } }],
    },
];
/**
 * How this section presents itself in "Add section": label, group, icon.
 *
 * It lives beside the schema, NOT in `index.ts`, and that is the point. `index.ts` is the module that pulls
 * in `Component.tsx`, and the builder reads `meta` for all sixty-two sections to paint the picker while
 * rendering only the handful a page actually uses. Keeping `meta` here lets `registry.lazy.gen.ts` import it
 * without dragging every section's render code into the editor's entry chunk.
 */
export const productGalleryMeta = {
    label: 'Product gallery',
    group: 'Product',
    icon: 'image',
};
//# sourceMappingURL=schema.js.map